import { Suspense, lazy, type ComponentType } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
// components
import LoadingScreen from '../components/LoadingScreen';
// guards
import { DashboardIndexRedirect, RequireAuth, RequireRole } from './guards';

// ----------------------------------------------------------------------

function Loadable<P extends object>(Component: ComponentType<P>) {
  return (props: P) => (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );
}

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    // Public site
    {
      path: '/',
      element: <MainLayout />,
      children: [
        { index: true, element: <Home /> },

        // Job board
        { path: 'jobs', element: <JobList /> },
        { path: 'jobs/:id', element: <JobDetail /> },
        { path: 'companies', element: <PublicDiscovery section="companies" /> },
        { path: 'recruiting/interview/:token', element: <RecruitingInvitation /> },
        { path: 'companies/:id', element: <PublicDiscovery section="company" /> },
        { path: 'salary-insights', element: <PublicDiscovery section="salaries" /> },

        // The original Job Victory contest
        { path: 'contest', element: <Contest /> },
        { path: 'contest/register', element: <ContestRegister /> },
        { path: 'quiz', element: <Quiz /> },
        { path: 'congratulation', element: <Congratulation /> },
        { path: 'pricing', element: <Pricing /> },

        // Info
        { path: 'about-us', element: <About /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <Faqs /> },
        { path: 'terms-of-service', element: <TermsOfService /> },
        { path: 'refund-policy', element: <RefundPolicy /> },
      ],
    },

    // Auth — these pages bring their own layout
    {
      path: 'auth',
      children: [
        { index: true, element: <Navigate to="/auth/login" replace /> },
        { path: 'login', element: <Login /> },
        { path: 'signup', element: <Signup /> },
        // The contest entry form used to live here.
        { path: 'register', element: <Navigate to="/contest/register" replace /> },
      ],
    },

    // Dashboards
    {
      path: 'dashboard',
      element: (
        <RequireAuth>
          <DashboardLayout />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <DashboardIndexRedirect /> },
        { path: 'notifications', element: <Notifications /> },
        { path: 'support', element: <Notifications support /> },

        {
          path: 'employer',
          element: (
            <RequireRole role="employer">
              <EmployerOutlet />
            </RequireRole>
          ),
          children: [
            { index: true, element: <EmployerOverview /> },
            { path: 'jobs', element: <EmployerJobs /> },
            { path: 'jobs/new', element: <EmployerJobForm /> },
            { path: 'jobs/:id/edit', element: <EmployerJobForm /> },
            { path: 'jobs/:id/applicants', element: <EmployerApplications scopedToJob /> },
            { path: 'applications', element: <EmployerApplications /> },
            { path: 'messages', element: <Messages /> },
            { path: 'profile', element: <EmployerProfile /> },
            { path: 'candidates', element: <CandidateSearch /> },
            { path: 'pipeline', element: <HiringPipeline /> },
            { path: 'interviews', element: <Interviews /> },
            { path: 'billing', element: <EmployerBilling /> },
            { path: 'ai', element: <AIManager /> },
          ],
        },

        {
          path: 'employee',
          element: (
            <RequireRole role="employee">
              <EmployerOutlet />
            </RequireRole>
          ),
          children: [
            { index: true, element: <EmployeeOverview /> },
            { path: 'applications', element: <EmployeeApplications /> },
            { path: 'messages', element: <Messages /> },
            { path: 'profile', element: <EmployeeProfile /> },
            { path: 'saved-jobs', element: <JobCollection /> },
            { path: 'job-matches', element: <JobCollection matches /> },
            { path: 'interviews', element: <Interviews /> },
            { path: 'resume-builder', element: <ResumeBuilder /> },
          ],
        },
        {
          path: 'admin',
          element: (
            <RequireRole role="admin">
              <EmployerOutlet />
            </RequireRole>
          ),
          children: [
            { index: true, element: <AdminOverview /> },
            { path: 'users', element: <AdminManagement section="users" /> },
            { path: 'employers', element: <AdminManagement section="employers" /> },
            { path: 'jobs', element: <AdminManagement section="jobs" /> },
            { path: 'imports', element: <AdminJobImports /> },
            { path: 'candidate-database', element: <AdminCandidateDatabase /> },
            { path: 'categories', element: <AdminManagement section="categories" /> },
            { path: 'payments', element: <AdminBilling /> },
            { path: 'reports', element: <AdminSettingsReports section="reports" /> },
            { path: 'settings', element: <AdminSettingsReports section="settings" /> },
          ],
        },
      ],
    },

    // Status pages
    { path: '404', element: <Page404 /> },
    { path: '500', element: <Page500 /> },
    { path: 'coming-soon', element: <ComingSoon /> },
    { path: 'maintenance', element: <Maintenance /> },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// ----------------------------------------------------------------------
// Pages are code-split so the landing page does not carry the legal copy,
// the question banks or the dashboards.
// ----------------------------------------------------------------------

const Home = Loadable(lazy(() => import('../pages/Home')));
const JobList = Loadable(lazy(() => import('../pages/jobs/JobList')));
const JobDetail = Loadable(lazy(() => import('../pages/jobs/JobDetail')));

const Contest = Loadable(lazy(() => import('../pages/contest/Contest')));
const ContestRegister = Loadable(lazy(() => import('../pages/contest/ContestRegister')));
const Quiz = Loadable(lazy(() => import('../pages/contest/Quiz')));
const Congratulation = Loadable(lazy(() => import('../pages/contest/Congratulation')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));

const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const TermsOfService = Loadable(lazy(() => import('../pages/TermsOfService')));
const RefundPolicy = Loadable(lazy(() => import('../pages/RefundPolicy')));

const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Signup = Loadable(lazy(() => import('../pages/auth/Signup')));

const EmployerOutlet = Loadable(lazy(() => import('./RoleOutlet')));
const EmployerOverview = Loadable(lazy(() => import('../pages/dashboard/EmployerOverview')));
const EmployerJobs = Loadable(lazy(() => import('../pages/dashboard/EmployerJobs')));
const EmployerJobForm = Loadable(lazy(() => import('../pages/dashboard/EmployerJobForm')));
const EmployerApplications = Loadable(
  lazy(() => import('../pages/dashboard/EmployerApplications'))
);
const EmployerProfile = Loadable(lazy(() => import('../pages/dashboard/EmployerProfile')));
const AIManager = Loadable(lazy(() => import('../pages/dashboard/AIManager')));
const RecruitingInvitation = Loadable(lazy(() => import('../pages/RecruitingInvitation')));
const AdminCandidateDatabase = Loadable(lazy(() => import('../sections/ai/CandidateDatabase').then((module) => ({ default: module.AdminCandidateDatabase }))));

const EmployeeOverview = Loadable(lazy(() => import('../pages/dashboard/EmployeeOverview')));
const EmployeeApplications = Loadable(
  lazy(() => import('../pages/dashboard/EmployeeApplications'))
);
const EmployeeProfile = Loadable(lazy(() => import('../pages/dashboard/EmployeeProfile')));

const Messages = Loadable(lazy(() => import('../pages/dashboard/Messages')));
const PublicDiscovery = Loadable(lazy(() => import('../pages/PublicDiscovery')));
const JobCollection = Loadable(lazy(() => import('../pages/dashboard/JobCollection')));
const CandidateSearch = Loadable(lazy(() => import('../pages/dashboard/CandidateSearch')));
const HiringPipeline = Loadable(lazy(() => import('../pages/dashboard/HiringPipeline')));
const Interviews = Loadable(lazy(() => import('../pages/dashboard/Interviews')));
const ResumeBuilder = Loadable(lazy(() => import('../pages/dashboard/ResumeBuilder')));
const EmployerBilling = Loadable(lazy(() => import('../pages/dashboard/EmployerBilling')));
const Notifications = Loadable(lazy(() => import('../pages/dashboard/Notifications')));
const AdminOverview = Loadable(lazy(() => import('../pages/admin/AdminOverview')));
const AdminManagement = Loadable(lazy(() => import('../pages/admin/AdminManagement')));
const AdminJobImports = Loadable(lazy(() => import('../pages/admin/AdminJobImports')));
const AdminBilling = Loadable(lazy(() => import('../pages/admin/AdminBilling')));
const AdminSettingsReports = Loadable(lazy(() => import('../pages/admin/AdminSettingsReports')));

const Page404 = Loadable(lazy(() => import('../pages/Page404')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
